import { useState } from 'react';
import { Link, useParams } from 'react-router';
import Button from '@/components/UI/Button';
import LoadingTimeline from '@/components/UI/loading/LoadingTimeline';
import type { IGetAuditsServiceResData } from '../../../../../../api/Terminal/types';
import useAudits from '../../../../../../hooks/ReactQueryHooks/useTerminalQuery';
import { getElapsedTime } from '../../../../../../utils';

function classNames(...classes: any) {
  return classes.filter(Boolean).join(' ');
}

export const ProjectAudit = () => {
  const { projectId } = useParams();
  const [params, setParams] = useState({
    page: 1,
  });

  const fetchProjectAudits = useAudits({
    queryKey: 'GET_SHERPA_PROJECT_AUDITS',
    resource_type: 'project',
    resource_id: Number(projectId),
    params,
  });
  const projectAudits = fetchProjectAudits?.data?.data;

  const pageChangeHandler = (action: 'next' | 'prev') => {
    if (action === 'next' && projectAudits.length === 50) {
      setParams((prevState) => ({
        ...prevState,
        page: prevState.page + 1,
      }));
    }

    if (action === 'prev' && params.page > 1) {
      setParams((prevState) => ({
        ...prevState,
        page: prevState.page - 1,
      }));
    }
  };

  const formatDescription = (description: string) => {
    let formattedDescription = description.split(':')[1]?.trim();
    if (description.includes('created')) {
      formattedDescription = 'Created project';
    }

    if (description.includes('Task') || description.includes('Deliverable') || description.includes('Milestone')) {
      const tdmType = formattedDescription.split(' ')[0];
      const tdmId = formattedDescription.split(' ')[2];
      return (
        <>
          updated {tdmType}{' '}
          <Link to={`/terminal/dashboard/${tdmType}s/${tdmId}`} className='underline text-blue-500 cursor-pointer'>
            {tdmId}
          </Link>
        </>
      );
    }

    if (formattedDescription.includes('Added') && formattedDescription.includes('asset')) {
      formattedDescription = `uploaded file - ${formattedDescription.split('-')[1]}`;
    }
    return <>{formattedDescription}</>;
  };

  return (
    <section aria-labelledby='timeline-title' className='bg-white shadow sm:rounded-lg divide-y'>
      <div className='py-5'>
        <h2
          id='timeline-title'
          className='text-lg px-6 py-2 font-medium text-gray-900 m-0 border-l-4 border-primaryColor-normal text-primaryColor-normal'
        >
          Project History
        </h2>
      </div>
      <div className='py-6 flow-root px-6'>
        {fetchProjectAudits.isLoading && <LoadingTimeline />}
        {projectAudits?.length === 0 && <span>No Project History</span>}
        {!fetchProjectAudits.isLoading && (
          <dl className='space-y-5'>
            {projectAudits?.map((audit: IGetAuditsServiceResData) => (
              <dt key={audit.id} className='relative pb-5'>
                <span className='absolute top-4 left-1.5 -ml-px h-full w-0.5 bg-gray-200' aria-hidden='true' />
                <div className='min-w-0 flex space-x-4'>
                  <dt className='text-sm text-gray-500'>
                    <span className={classNames('h-3 w-3 bg-tertiaryColor-light rounded-full flex items-center justify-center')}></span>
                  </dt>
                  <dd className='flex flex-col text-sm whitespace-wrap text-gray-500'>
                    <p className='break-words'>
                      <strong>{audit.user.name}</strong> <span className='lowercase'>{formatDescription(audit.description)}</span>
                    </p>
                    <time dateTime={audit.createdAt} className='text-xs'>
                      {getElapsedTime(new Date(audit.createdAt).getTime() / 1000)}
                    </time>
                  </dd>
                </div>
              </dt>
            ))}
          </dl>
        )}
      </div>
      <div className='flex justify-end gap-4 p-4'>
        <Button
          label='Previous'
          btnStyle={`${params.page === 1 ? 'neutral' : 'primary'}`}
          btnType='button'
          onClick={() => pageChangeHandler('prev')}
          disabled={params.page === 1}
        />
        <Button
          label='Next'
          btnStyle={`${projectAudits?.length < 50 ? 'neutral' : 'primary'}`}
          btnType='button'
          onClick={() => pageChangeHandler('next')}
          disabled={projectAudits?.length < 50}
        />
      </div>
    </section>
  );
};
